import { useEffect, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Container, Flex, HStack, Heading, IconButton, Image, Skeleton,
  Slider, SliderFilledTrack, SliderThumb, SliderTrack, Spinner, Stack, Text, useToast,
} from '@chakra-ui/react';
import { mixgeniePublicServiceURL } from '../redux/services/defines';

/**
 * Public share page at /s/:jobId. Rendered without auth so a share link
 * lands on a working preview for anyone who taps it. The BE endpoint is
 * `/public/songs/:jobId` and returns ONLY safe fields (no lyrics, no
 * prompt, no user meta). The share value prop for the recipient:
 *   1. hear the song immediately
 *   2. see it's on MixGenie
 *   3. tap "Create your own" → funnel into signup
 *
 * There is no OG-tag SSR here yet — SPA with a CloudFront function is
 * the follow-up to make link previews on Slack/WhatsApp/iMessage render
 * with the cover art and title. For now the text preview says
 * "MixGenie — Made with AI".
 */
export default function PublicSongPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const toast = useToast();
  const [data, setData] = useState<null | {
    jobId: string;
    title: string;
    artist: string;
    coverImageUrl?: string;
    audioUrl?: string;
    instrumental?: boolean;
  }>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'notfound'>('loading');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!jobId) { setLoadState('notfound'); return; }
    let cancelled = false;
    (async () => {
      try {
        // The FE public gateway strips `public-gateway/mixgenie-service`
        // and forwards to the service at `/public/songs/:jobId`. Never use
        // `mixgenieServiceURL` here — that's the AUTH-gated gateway and
        // recipients of share links aren't logged in.
        const res = await fetch(`${mixgeniePublicServiceURL}/songs/${encodeURIComponent(jobId)}`);
        if (!res.ok) throw new Error(String(res.status));
        const body = await res.json();
        if (!cancelled) {
          setData(body?.data || null);
          setLoadState(body?.data ? 'ready' : 'notfound');
        }
      } catch {
        if (!cancelled) setLoadState('notfound');
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => toast({ title: 'Playback failed', status: 'error', duration: 2000 }));
    else a.pause();
  };

  const doShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: data?.title || 'A song on MixGenie',
          text: `Listen to "${data?.title || 'this'}" — made with MixGenie`,
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copied', status: 'success', duration: 1800 });
      } catch {
        toast({ title: url, status: 'info', duration: 4000 });
      }
    }
  };

  if (loadState === 'loading') {
    return (
      <Box minH="100vh" bgGradient="linear(180deg, #1a0f2e 0%, #0f0b1f 100%)" color="whiteAlpha.900" py={10}>
        <Container maxW="440px">
          <Skeleton height="440px" borderRadius="18px" mb={6} startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
          <Skeleton height="24px" mb={2} startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
          <Skeleton height="16px" width="60%" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
        </Container>
      </Box>
    );
  }

  if (loadState === 'notfound' || !data) {
    return (
      <Box minH="100vh" bgGradient="linear(180deg, #1a0f2e 0%, #0f0b1f 100%)" color="whiteAlpha.900" py={10}>
        <Container maxW="440px" textAlign="center">
          <Heading size="lg" mb={3}>Song not found</Heading>
          <Text color="whiteAlpha.700" mb={6}>
            The link may have expired or the song was deleted.
          </Text>
          <Button as={RouterLink} to="/generator" colorScheme="pink" size="lg">
            Make your own on MixGenie
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bgGradient="linear(180deg, #1a0f2e 0%, #0f0b1f 100%)" color="whiteAlpha.900">
      <Container maxW="480px" py={{ base: 6, md: 10 }}>
        <Flex justify="space-between" align="center" mb={6}>
          <HStack spacing={2} as={RouterLink} to="/generator">
            <Image src="/logo/logo_single.png" alt="" boxSize="28px" />
            <Text fontSize="lg" fontWeight={800} color="whiteAlpha.900">MixGenie</Text>
          </HStack>
          <IconButton
            aria-label="Share"
            variant="ghost"
            color="whiteAlpha.900"
            onClick={doShare}
            icon={
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            }
          />
        </Flex>

        {/* Cover */}
        <Box
          w="100%"
          aspectRatio={1}
          borderRadius="18px"
          overflow="hidden"
          boxShadow="0 20px 60px rgba(0,0,0,0.6)"
          mb={5}
        >
          {data.coverImageUrl ? (
            <Image src={data.coverImageUrl} alt="" w="100%" h="100%" objectFit="cover" />
          ) : (
            <Flex
              w="100%"
              h="100%"
              bgGradient="linear(135deg, pink.500, purple.600)"
              align="center"
              justify="center"
              color="white"
              fontSize={{ base: '80px', md: '110px' }}
            >
              {data.instrumental ? '🎹' : '🎵'}
            </Flex>
          )}
        </Box>

        <Stack spacing={1} textAlign="center" mb={5}>
          <Heading fontSize={{ base: 'xl', md: '2xl' }} noOfLines={2}>{data.title}</Heading>
          <Text color="whiteAlpha.700" fontSize="sm">{data.artist}</Text>
        </Stack>

        {/* Transport */}
        <audio
          ref={audioRef}
          src={data.audioUrl}
          preload="metadata"
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
        <Slider
          value={progress}
          min={0}
          max={duration || 1}
          step={0.1}
          onChange={(v) => {
            const a = audioRef.current;
            if (a) a.currentTime = v;
            setProgress(v);
          }}
          mb={3}
        >
          <SliderTrack bg="whiteAlpha.200"><SliderFilledTrack bg="pink.400" /></SliderTrack>
          <SliderThumb boxSize={3} />
        </Slider>
        <Flex justify="space-between" fontSize="xs" color="whiteAlpha.700" mb={5}>
          <Text>{formatTime(progress)}</Text>
          <Text>{formatTime(duration)}</Text>
        </Flex>

        <Flex justify="center" mb={8}>
          <IconButton
            aria-label={isPlaying ? 'Pause' : 'Play'}
            onClick={togglePlay}
            size="lg"
            boxSize="72px"
            borderRadius="full"
            bgGradient="linear(135deg, pink.400, purple.500)"
            color="white"
            _hover={{ opacity: 0.92 }}
            icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
          />
        </Flex>

        {/* Conversion CTA — the whole point of the public page. Sits under
            the player so a listener who's engaged (played the song) sees
            the invitation next. Free-credits copy pulls from the same env
            the register page uses. */}
        <Box
          bg="rgba(20, 14, 36, 0.7)"
          border="1px solid"
          borderColor="whiteAlpha.200"
          borderRadius="14px"
          p={5}
          textAlign="center"
          backdropFilter="blur(12px)"
        >
          <Text fontSize="sm" color="whiteAlpha.700" mb={1}>
            Made with MixGenie
          </Text>
          <Heading size="md" mb={3}>Create your own song</Heading>
          <Text fontSize="sm" color="whiteAlpha.700" mb={4}>
            Pick a reference, add a prompt, generate. Free credits on signup.
          </Text>
          <Stack spacing={2}>
            <Button
              as={RouterLink}
              to="/generator"
              colorScheme="pink"
              size="lg"
              w="full"
              data-cta="publicshare:create"
            >
              Try it free
            </Button>
            <Button
              as={RouterLink}
              to="/generator"
              variant="ghost"
              colorScheme="whiteAlpha"
              size="sm"
              w="full"
              data-cta="publicshare:learn"
            >
              How it works →
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

function formatTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="currentColor">
      <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
    </svg>
  );
}
